import { useState, useEffect } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useAnalyticsClient } from "@/analytics/wac-analytics";
import JiraSoftwareNav from "@/templates/software/jira/sections/nav-bar/software-jira-navbar";

import {
    EnrollmentType,
    experiments,
    useExperiment,
    useExperimentExposure
} from "../../../utils/experiment";
import { PaidJiraAiBackground } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-background";
import { PaidJiraAiBody } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-body";
import { PaidJiraAiDisclaimer } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-disclaimer";
import { PaidJiraAiHeader } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-header";
import { PaidJiraAiPreviewModal } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-preview-modal";
import { PaidJiraAiTeamTypeMadLibPrompt } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-team-type-mad-lib-prompt";
import { PaidJiraAiHeaderV2 } from "../sections/cfind-6215-frictionless-first-look-jira-v2";

import {
    JiraAiFFLProvider,
    useJiraAiFFL
} from "./hooks/use-jira-ai-ffl-context";

const promptContainerCss = css({
    marginBlockStart: "48px",
    padding: "0 60px",
    position: "relative",
    textAlign: "center",
    isolation: "isolate"
});

const promptContentCss = css({
    position: "relative",
    zIndex: 1
});

export const JiraProductTourAiTemplateContent = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();
    const { cgsDataLoading } = useJiraAiFFL();
    const [jiraFflV2ExposureFired, fireJiraFflV2Exposure] =
        useExperimentExposure(experiments.cfind6215FrictionlessFirstLookJiraV2);
    const jiraFflV2Experiment = useExperiment(
        experiments.cfind6215FrictionlessFirstLookJiraV2
    );
    const isJiraFflV2 =
        jiraFflV2Experiment.type === EnrollmentType.ENROLLED &&
        jiraFflV2Experiment.value === true;

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

    useEffect(() => {
        if (
            jiraFflV2Experiment.type === EnrollmentType.ENROLLED &&
            !jiraFflV2ExposureFired
        ) {
            fireJiraFflV2Exposure();
        }
    }, [
        jiraFflV2Experiment.type,
        jiraFflV2ExposureFired,
        fireJiraFflV2Exposure
    ]);

    useEffect(() => {
        if (cgsDataLoading) {
            setIsPreviewModalOpen(true);
        }
    }, [cgsDataLoading]);

    useEffect(() => {
        if (!analyticsClient) {
            return; // be sure analyticsClient is available before firing event
        }
        createAnalyticsEvent({
            action: "viewed",
            eventType: "screen",
            name: "PaidLandingJiraAiTemplate",
            attributes: {
                flow: "paidLandingJiraAi",
                variant: "v2"
            }
        }).fire();
    }, [analyticsClient, createAnalyticsEvent, isJiraFflV2]);

    return (
        <main>
            <JiraSoftwareNav />
            {isJiraFflV2 ? (
                <PaidJiraAiHeaderV2 />
            ) : (
                <>
                    <PaidJiraAiHeader />
                    <div css={promptContainerCss}>
                        <PaidJiraAiBackground />
                        <div css={promptContentCss}>
                            <PaidJiraAiTeamTypeMadLibPrompt />
                        </div>
                    </div>
                </>
            )}
            <PaidJiraAiDisclaimer />
            {!isJiraFflV2 && isPreviewModalOpen && (
                <PaidJiraAiPreviewModal
                    onClose={() => setIsPreviewModalOpen(false)}
                />
            )}
            <PaidJiraAiBody />
        </main>
    );
};

export const JiraProductTourAiTemplate = () => (
    <JiraAiFFLProvider>
        <JiraProductTourAiTemplateContent />
    </JiraAiFFLProvider>
);
